/**
 * Size Guide Modal Component
 * Supports real-time CM / INCH unit conversions, accessible modal controls,
 * automatic fallback table generation, and responsive styling for Shopify product pages.
 */

class SizeGuideModal extends HTMLElement {
  constructor() {
    super();
    this.currentUnit = 'cm';
    this.originalData = null;
    this.lastActiveElement = null;
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog.size-guide-dialog');
    this.closeBtn = this.querySelector('[data-size-guide-close]');
    this.unitButtons = this.querySelectorAll('[data-unit]');
    this.tableWrap = this.querySelector('.size-guide-table-wrap');
    this.scrollbar = this.querySelector('[data-size-guide-scrollbar]');
    this.scrollThumb = this.querySelector('[data-size-guide-thumb]');
    this.dataScript = this.querySelector('.size-guide-data');
    this.bodyContainer = this.querySelector('.size-guide-dialog__body');

    if (this.dataScript) {
      try {
        const rawContent = this.dataScript.textContent.trim();
        this.originalData = typeof rawContent === 'string' && rawContent.startsWith('{')
          ? JSON.parse(rawContent)
          : rawContent;

        if (this.originalData && typeof this.originalData === 'object') {
          if (this.originalData.guide_des_tailles) {
            this.originalData = this.originalData.guide_des_tailles;
          }
          if (this.originalData.unit) {
            this.currentUnit = this.originalData.unit.toLowerCase();
          }
          // If table was not rendered server-side (e.g. metafield is text type), build it client-side
          if (!this.querySelector('.size-guide-table') && this.originalData.columns && this.originalData.rows) {
            this.renderContentFromData();
          }
        }
      } catch (e) {
        console.error('Error parsing size guide JSON:', e);
      }
    }

    this.cacheTableElements();
    this.initEvents();
  }

  cacheTableElements() {
    this.tableWrap = this.querySelector('.size-guide-table-wrap');
    this.scrollbar = this.querySelector('[data-size-guide-scrollbar]');
    this.scrollThumb = this.querySelector('[data-size-guide-thumb]');
    this.table = this.querySelector('.size-guide-table');
    this.headerCells = this.querySelectorAll('.size-guide-table th');
    this.bodyCells = this.querySelectorAll('.size-guide-table td[data-val]');
  }

  updateScrollbar() {
    if (!this.tableWrap) return;

    if (!this.scrollbar || !this.scrollThumb) {
      this.cacheTableElements();
    }

    const { scrollWidth, clientWidth, scrollLeft } = this.tableWrap;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll > 2) {
      if (this.scrollbar) {
        this.scrollbar.style.display = 'block';
      }
      const thumbWidthPercent = Math.max(18, Math.min(80, (clientWidth / scrollWidth) * 100));
      const availableTrackPercent = 100 - thumbWidthPercent;
      const thumbOffsetPercent = maxScroll > 0 ? (scrollLeft / maxScroll) * availableTrackPercent : 0;

      if (this.scrollThumb) {
        this.scrollThumb.style.width = `${thumbWidthPercent}%`;
        this.scrollThumb.style.transform = `translateX(${(thumbOffsetPercent / thumbWidthPercent) * 100}%)`;
      }

      this.tableWrap.dataset.hasMoreRight = scrollLeft < maxScroll - 2 ? 'true' : 'false';
      this.tableWrap.dataset.hasMoreLeft = scrollLeft > 2 ? 'true' : 'false';
    } else {
      if (this.scrollbar) {
        this.scrollbar.style.display = 'none';
      }
      this.tableWrap.dataset.hasMoreRight = 'false';
      this.tableWrap.dataset.hasMoreLeft = 'false';
    }
  }

  hasMultiSystem() {
    // 1. Check data-has-multi-systems attribute on modal component
    if (this.dataset.hasMultiSystems === 'true') return true;
    if (this.dataset.hasMultiSystems === 'false') return false;

    // 2. Check original data rows: does any size string contain "/" and ("eu" or "us")?
    if (Array.isArray(this.originalData?.rows)) {
      return this.originalData.rows.some((r) => {
        const s = String(r?.size || '').toLowerCase();
        return s.includes('/') && (s.includes('eu') || s.includes('us'));
      });
    }

    // 3. Fallback check variant switcher on page
    if (document.querySelector('[data-size-system-switcher]')) return true;

    return false;
  }

  updateSizeSystem(system) {
    const sys = (system || localStorage.getItem('shopify_size_system') || 'EU').toUpperCase();
    const sysLower = sys.toLowerCase();
    this.currentSizeSystem = sys;

    // Auto-switch unit: EU -> cm, US / UK -> inch
    const autoUnit = sys === 'EU' ? 'cm' : 'inch';
    this.setUnit(autoUnit);

    const isMulti = this.hasMultiSystem();

    const DEFAULT_CONVERSIONS = {
      '33': { eu: '33', us: '3.5', uk: '1' },
      '34': { eu: '34', us: '4', uk: '1.5' },
      '35': { eu: '35', us: '5', uk: '2.5' },
      '36': { eu: '36', us: '5.5', uk: '3.5' },
      '37': { eu: '37', us: '6', uk: '4' },
      '38': { eu: '38', us: '7', uk: '5' },
      '39': { eu: '39', us: '8', uk: '6' },
      '40': { eu: '40', us: '9', uk: '6.5' },
      '41': { eu: '41', us: '9.5', uk: '7.5' },
      '42': { eu: '42', us: '10.5', uk: '8.5' },
      '43': { eu: '43', us: '11', uk: '9' },
      '44': { eu: '44', us: '12', uk: '10' },
    };

    // 1. Update the size column header
    const sizeHeader = this.querySelector('th[data-is-size-col="true"], th:first-child');
    if (sizeHeader) {
      let baseHeader = sizeHeader.dataset.originalHeader || sizeHeader.textContent.trim();
      baseHeader = baseHeader.replace(/\s*\([A-Za-z0-9\s/_-]+\)\s*/gi, '').trim();
      if (!sizeHeader.dataset.originalHeader) {
        sizeHeader.dataset.originalHeader = baseHeader;
      }
      sizeHeader.textContent = isMulti ? `${baseHeader} (${sys})` : baseHeader;
    }

    if (!isMulti) {
      this.updateScrollbar();
      return;
    }

    // 2. Map from product buttons if available
    const productVariantMap = {};
    document.querySelectorAll('.variant-option__button-label[data-size-eu]').forEach((label) => {
      const eu = label.getAttribute('data-size-eu');
      const us = label.getAttribute('data-size-us');
      const uk = label.getAttribute('data-size-uk');
      if (eu) {
        productVariantMap[eu] = { eu, us, uk };
      }
    });

    // 3. Update each row's size value
    this.querySelectorAll('td.size-guide-cell--size').forEach((td) => {
      let targetSize = td.getAttribute(`data-size-${sysLower}`);
      const euSize = td.getAttribute('data-size-eu') || td.textContent.trim().replace(/[^0-9.]/g, '');

      if (!targetSize && euSize) {
        const mapped = productVariantMap[euSize] || DEFAULT_CONVERSIONS[euSize];
        if (mapped && mapped[sysLower]) {
          targetSize = mapped[sysLower];
        }
      }

      const strong = td.querySelector('[data-size-guide-row-size]') || td.querySelector('strong') || td;
      if (targetSize && strong) {
        strong.textContent = targetSize;
      }
    });

    this.updateScrollbar();
  }

  renderContentFromData() {
    if (!this.bodyContainer || !this.originalData) return;

    // Advice if not present
    if (this.originalData.fit_advice && !this.querySelector('.size-guide-advice')) {
      const adviceEl = document.createElement('div');
      adviceEl.className = 'size-guide-advice';
      adviceEl.innerHTML = `
        <div class="size-guide-advice__icon">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </div>
        <div class="size-guide-advice__content">
          <span class="size-guide-advice__tag">Conseil taille</span>
          <p class="size-guide-advice__text">${this.escapeHtml(this.originalData.fit_advice)}</p>
        </div>
      `;
      this.bodyContainer.prepend(adviceEl);
    }

    // Table
    if (this.originalData.columns && this.originalData.rows) {
      let wrap = this.querySelector('.size-guide-table-wrap');
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'size-guide-table-wrap';
        this.bodyContainer.appendChild(wrap);
      }

      let cols = [...this.originalData.columns];
      const firstCol = String(cols[0] || '').toLowerCase();
      const hasSizeCol = firstCol === 'taille' || firstCol === 'size' || firstCol === 'pointure';
      const isMulti = this.hasMultiSystem();

      let ths = '';
      if (!hasSizeCol) {
        const headerText = isMulti ? `Taille (${this.currentSizeSystem || 'EU'})` : 'Taille';
        ths += `<th scope="col" data-original-header="Taille" data-is-size-col="true">${headerText}</th>`;
      }

      ths += cols.map((c) => {
        let isSize = /taille|size|pointure/i.test(c);
        let headerText = isSize && isMulti ? `${this.escapeHtml(c)} (${this.currentSizeSystem || 'EU'})` : this.escapeHtml(c);
        return `<th scope="col" data-original-header="${this.escapeHtml(c)}" ${isSize ? 'data-is-size-col="true"' : ''}>${headerText}</th>`;
      }).join('');

      let trs = this.originalData.rows.map((row) => {
        let rawSize = String(row.size || '');
        let eu = rawSize;
        let us = '';
        let uk = '';

        if (rawSize.includes('/') && isMulti) {
          rawSize.split('/').forEach((p) => {
            let pTrimmed = p.trim();
            let pUp = pTrimmed.toUpperCase();
            let clean = pTrimmed.replace(/^(EU|US|UK|FR)\s*|\s*(EU|US|UK|FR)$/gi, '').trim();
            if (pUp.includes('EU') || pUp.includes('FR')) eu = clean;
            else if (pUp.includes('US')) us = clean;
            else if (pUp.includes('UK')) uk = clean;
          });
        }

        let sizeDataAttrs = isMulti ? `data-size-eu="${this.escapeHtml(eu)}" data-size-us="${this.escapeHtml(us)}" data-size-uk="${this.escapeHtml(uk)}"` : '';
        let vals = (row.values || []).map((v) => `<td data-val="${this.escapeHtml(v)}">${this.escapeHtml(v)}</td>`).join('');
        return `<tr><td class="size-guide-cell--size" ${sizeDataAttrs}><strong data-size-guide-row-size>${this.escapeHtml(rawSize)}</strong></td>${vals}</tr>`;
      }).join('');

      wrap.innerHTML = `
        <table class="size-guide-table">
          <thead><tr>${ths}</tr></thead>
          <tbody>${trs}</tbody>
        </table>
      `;

      if (!this.querySelector('.size-guide-scrollbar')) {
        const scrollbarEl = document.createElement('div');
        scrollbarEl.className = 'size-guide-scrollbar';
        scrollbarEl.setAttribute('data-size-guide-scrollbar', '');
        scrollbarEl.setAttribute('aria-hidden', 'true');
        scrollbarEl.innerHTML = '<div class="size-guide-scrollbar__track"><div class="size-guide-scrollbar__thumb" data-size-guide-thumb></div></div>';
        this.bodyContainer.appendChild(scrollbarEl);
      }
    }
  }

  escapeHtml(str) {
    if (typeof str !== 'string') return str;
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  initEvents() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    }

    // Table scroll listener
    if (this.tableWrap) {
      this.tableWrap.addEventListener('scroll', () => this.updateScrollbar(), { passive: true });
    }
    window.addEventListener('resize', () => this.updateScrollbar(), { passive: true });

    // Listen for size system changes from product page
    document.addEventListener('change', (e) => {
      const switcher = e.target?.closest?.('[data-size-system-switcher]');
      if (switcher) {
        this.updateSizeSystem(switcher.value);
      }
    });

    // Backdrop click
    if (this.dialog) {
      this.dialog.addEventListener('click', (e) => {
        const dialogDimensions = this.dialog.getBoundingClientRect();
        if (
          e.clientX < dialogDimensions.left ||
          e.clientX > dialogDimensions.right ||
          e.clientY < dialogDimensions.top ||
          e.clientY > dialogDimensions.bottom
        ) {
          this.close();
        }
      });

      // Escape key
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });

      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        }
      });
    }

    // Unit toggle buttons
    this.unitButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetUnit = btn.dataset.unit;
        if (targetUnit && targetUnit !== this.currentUnit) {
          this.setUnit(targetUnit);
        }
      });
    });
  }

  setUnit(unit) {
    this.currentUnit = unit;
    this.cacheTableElements();

    // Update active button state
    this.unitButtons.forEach((btn) => {
      if (btn.dataset.unit === unit) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    // Update Column Headers (cm <-> inch)
    this.headerCells.forEach((th) => {
      if (th.getAttribute('data-is-size-col') === 'true') return;

      const originalHeader = th.dataset.originalHeader || th.textContent.trim();
      if (!th.dataset.originalHeader) {
        th.dataset.originalHeader = originalHeader;
      }

      if (unit === 'inch') {
        th.textContent = originalHeader
          .replace(/\(cm\)/gi, '(inch)')
          .replace(/\(CM\)/g, '(INCH)')
          .replace(/ en cm/gi, ' en inch');
      } else {
        th.textContent = originalHeader;
      }
    });

    // Update Body Cells (convert values)
    this.bodyCells.forEach((td) => {
      const originalVal = td.dataset.val;
      if (!originalVal) return;

      if (unit === 'inch') {
        td.textContent = this.convertCmToInch(originalVal);
      } else {
        td.textContent = originalVal;
      }
    });

    this.updateScrollbar();
  }

  /**
   * Converts a numeric string or range from cm to inches.
   * e.g. "21.5-22.0" -> "8.5-8.7"
   * e.g. "8.5" -> "3.3"
   */
  convertCmToInch(valStr) {
    if (!valStr || typeof valStr !== 'string') return valStr;

    // Match numbers with optional decimal (dot or comma)
    return valStr.replace(/\d+(?:[.,]\d+)?/g, (match) => {
      const numCm = parseFloat(match.replace(',', '.'));
      if (isNaN(numCm)) return match;
      const numInch = numCm / 2.54;
      // Round to 1 decimal place
      return (Math.round(numInch * 10) / 10).toFixed(1);
    });
  }

  open(triggerElement = null) {
    if (!this.dialog) return;

    this.lastActiveElement = triggerElement || document.activeElement;

    // Sync with currently active size system on the product page
    const currentSwitcher = document.querySelector('[data-size-system-switcher]');
    const activeSystem = currentSwitcher?.value || localStorage.getItem('shopify_size_system') || 'EU';
    this.updateSizeSystem(activeSystem);

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }

    document.body.classList.add('size-guide-locked');
    this.dialog.classList.remove('is-closing');
    
    // Trigger transition
    requestAnimationFrame(() => {
      this.dialog.classList.add('is-open');
      if (this.closeBtn) {
        this.closeBtn.focus();
      }
      this.updateScrollbar();
      setTimeout(() => this.updateScrollbar(), 50);
    });
  }

  close() {
    if (!this.dialog || !this.dialog.open) return;

    this.dialog.classList.remove('is-open');
    this.dialog.classList.add('is-closing');

    const handleClose = () => {
      this.dialog.classList.remove('is-closing');
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      document.body.classList.remove('size-guide-locked');

      if (this.lastActiveElement && typeof this.lastActiveElement.focus === 'function') {
        this.lastActiveElement.focus();
      }
    };

    setTimeout(handleClose, 220);
  }
}

if (!customElements.get('size-guide-modal')) {
  customElements.define('size-guide-modal', SizeGuideModal);
}

// Global click handler for trigger buttons across dynamically loaded or morphed sections
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-size-guide-open], .size-guide-trigger');
  if (!trigger) return;

  event.preventDefault();
  const targetId = trigger.getAttribute('data-size-guide-open') || trigger.getAttribute('aria-controls');
  let modal = targetId ? document.getElementById(targetId) : null;

  if (!modal) {
    modal = document.querySelector('size-guide-modal');
  }

  if (modal && typeof modal.open === 'function') {
    modal.open(trigger);
  }
});
